import * as d3 from 'd3';
import * as _ from 'lodash';

import { CategoricalChartData } from './CategoricalChart.model';
import { XAxis } from './xAxis';
import { YAxis } from './yAxis';
import { Bars } from './bars';
import { Circles } from './circles';
import { ElementRender } from './ElementeRenderer';
import { Line } from './line';
import { Labels } from './labels';
import { MinMax } from './minMax';
import { HorizontalLines } from './horizontalLines';
import { Tooltip } from './tooltip';
import { CrosshairLine } from './crosshairLine';
import { IHeight, IWidth, ItemFormat, IRender } from '../interfaces';

export class CategoricalChartRenderer {
    private _width: number;
    private _height: number;
    private _xAxis: XAxis;
    private _yAxis: YAxis;
    private _widths: IWidth[] = [];
    private _heights: IHeight[] = [];
    private _options: { [object: string]: boolean; } = {};
    private _renderers: { [object: string]: (ElementRender & IRender) } = {};
    private _horizontalLines: HorizontalLines;
    private _tooltip: Tooltip;
    private _crosshairLine: CrosshairLine;

    _itemFormats: ItemFormat[];

    constructor(selection: d3.Selection<any>, width: number, height: number, clipPathId: string) {
        selection.classed('categorical-chart', true);
        this._xAxis = new XAxis(selection, width, height);
        this._widths.push(this._xAxis);
        this._heights.push(this._xAxis);
        this._yAxis = new YAxis(selection, width, height);
        this._widths.push(this._yAxis);
        this._heights.push(this._yAxis);
        this._horizontalLines = new HorizontalLines(selection, 'horizontal-gridLines')
            .classes('gridline');
        const seriesContainer = selection.append('g')
            .classed('series', true)
            .style({ 'clip-path': `url(#${clipPathId})` });
        const bars = new Bars(seriesContainer)
            .visible(true)
            .addListener('mouseenter', (d, selection) => {
                const crosshairWidth = this._tooltip.width();
                const crosshairHeight = this._tooltip.height();
                let x = d.x + d.width;
                if (x + crosshairWidth > this._width) {
                    x = d.x - crosshairWidth;
                }
                let y = d.y - (d.item.value < 0 ? d.height : 0);
                if (y + crosshairHeight > this._height) {
                    y -= crosshairHeight;
                } else if (y < 0) {
                    y = 0;
                }
                this._tooltip.visible(true)
                    .position({ x, y })
                    .render(d);
            })
            .addListener('mouseout', (d, selection) => {
                this._tooltip.visible(false)
                    .render(d);
            });
        this._renderers['bars'] = bars;
        const circles = new Circles(seriesContainer)
            .visible(true)
            .addListener('mouseenter', (d, selection) => {
                const crosshairWidth = this._tooltip.width();
                const crosshairHeight = this._tooltip.height();
                const r = circles.radius();
                let x = d.x + r + d.width / 2;
                if (x + crosshairWidth > this._width) {
                    x = d.x + d.width / 2 - r - crosshairWidth;
                }
                let y = d.y;
                if (y + crosshairHeight > this._height) {
                    y -= crosshairHeight;
                } else if (y < 0) {
                    y = 0;
                }
                this._tooltip.visible(true)
                    .position({ x, y })
                    .render(d);
            })
            .addListener('mouseout', (d, selection) => {
                this._tooltip.visible(false)
                    .render(d);
            });
        this._renderers['circles'] = circles;
        const line = new Line(seriesContainer)
            .visible(true);
        this._renderers['line'] = line;
        this._renderers['minmax'] = new MinMax(seriesContainer);
        this._renderers['labels'] = new Labels(seriesContainer);
        this._widths.push(this._horizontalLines);
        this._heights.push(this._horizontalLines);
        this._tooltip = new Tooltip(seriesContainer);
        this._crosshairLine = new CrosshairLine(seriesContainer)
            .addListener('mouseenter', (d, selection) => {
                this._tooltip.visible(true);
            })
            .addListener('mousemove', (d, selection) => {
                const pos = this._xAxis.index(d[0]);
                if (pos !== undefined && this._itemFormats) {
                    const itemFormat = this._itemFormats[pos];
                    let x = itemFormat.x + itemFormat.width / 2;
                    const crosshairWidth = this._tooltip.width();
                    if (x + crosshairWidth > this._width) {
                        x -= crosshairWidth;
                    }
                    this._tooltip//.visible(true)
                        .position({ x, y: 0 })
                        .render(itemFormat);
                    this._crosshairLine.render(itemFormat);
                }
            })
            .addListener('mouseout', (d, selection) => {
                this._tooltip.visible(false)
                    .render(null);
            });
        this._widths.push(this._crosshairLine);
        this._heights.push(this._crosshairLine);
        this.width(width)
            .height(height);
    }

    color(): string;
    color(value: string): this;
    color(value?: string): this | string {
        const line = this._renderers['line'] as Line;
        if (!arguments.length) {
            return line.color();
        }
        line.color(value);
        return this;
    }

    height(): number;
    height(value: number): this;
    height(value?: number): this | number {
        if (!arguments.length) {
            return this._height;
        }
        this._heights.forEach(r => r.height(value));
        for (const key of Object.keys(this._renderers)) {
            this._renderers[key].height(value);
        }
        this._height = value;
        return this;
    }

    width(): number;
    width(value: number): this;
    width(value?: number): this | number {
        if (!arguments.length) {
            return this._width;
        }
        this._widths.forEach(r => r.width(value));
        for (const key of Object.keys(this._renderers)) {
            this._renderers[key].width(value);
        }
        this._width = value;
        return this;
    }

    private _ratio: number = 1;
    ratio(): number;
    ratio(value: number): this;
    ratio(value?: number) {
        if (arguments.length) {
            this._ratio = value;
            for (const key of Object.keys(this._renderers)) {
                if (this._renderers[key].ratio) {
                    this._renderers[key].ratio(value);
                }
            }
            return this;
        }
        return this._ratio;
    }

    yDomain(): number[];
    yDomain(value: number[]): this;
    yDomain(value?: number[]): this | number[] {
        if (!arguments.length) {
            return this._yAxis.extent();
        }
        this._yAxis.extent(value);
        return this;
    }

    spacing(): number;
    spacing(value: number): this;
    spacing(value?: number): this | number {
        if (!arguments.length) {
            return this._xAxis.spacing();
        }
        this._xAxis.spacing(value);
        return this;
    }

    options(): { [object: string]: boolean };
    options(value: { [object: string]: boolean }): this;
    options(value?: { [object: string]: boolean }): this | { [object: string]: boolean } {
        if (!arguments.length) {
            return this._options;
        }
        this._options = value;
        return this;
    }

    render(data: CategoricalChartData[]): void {
        this._xAxis.render(data);
        this._yAxis.render(data);
        const barFormat: ItemFormat[] = this.toItemFormat(data);
        this._itemFormats = barFormat;
        for (const key of Object.keys(this._renderers)) {
            const isVisible = this._options[key];
            this._renderers[key]
                .visible(isVisible)
                .render(barFormat);
        }
        this._horizontalLines
            .visible(this._options['horizontalLines'])
            .render(this._yAxis.ticks().map(d => {
                return { y: this._yAxis.position(d) };
            }));
        this._crosshairLine
            .visible(!this._options['bars'] && !this._options['circles'])
            .render(null);
    }

    private toItemFormat(data: CategoricalChartData[],) {
        const zero = this._yAxis.position(0);

        return data.map(d => {
            let yPos = this._yAxis.position(d.value);
            const height = Math.abs(yPos - zero);
            const translateY = d.value < 0 ? -height : 0;
            if (d.offset) {
                yPos = this._yAxis.position(d.value + d.offset);
            }
            const res: ItemFormat = {
                item: d,
                x: this._xAxis.position(d),
                y: yPos,
                width: this._xAxis.band(),
                height,
                color: d.color,
                y1: d.hasMin ? this._yAxis.position(d.min) : 0,
                y2: d.hasMax ? this._yAxis.position(d.max) : 0,
                translate: {
                    x: 0,
                    y: translateY
                }
            };
            return res;
        });
    }
}
